import { ref } from 'vue';
import apiClient from '@/api/client';

/**
 * Feature flags loaded from GET /api/config/features at app boot.
 *
 * The backend is the source of truth — these flags are read once per session
 * and cached in a module-level ref. Components read `flags.value` to gate UI.
 * Gating is purely visual: the backend also enforces each flag on its
 * corresponding endpoint, so flipping a flag off on the frontend never creates
 * a security gap.
 *
 * Defaults to all-false before load completes, so gated UI is hidden during
 * the initial fetch rather than flashing on.
 */

export interface FeatureFlags {
  analystCrud: boolean;
  quickAdd: boolean;
  bulkImport: boolean;
  crossPivots: boolean;
  globalSearch: boolean;
  reportEditor: boolean;
  activityFeed: boolean;
  iaRefactor: boolean;
  collabLoop: boolean;
}

const defaults: FeatureFlags = {
  analystCrud: false,
  quickAdd: false,
  bulkImport: false,
  crossPivots: false,
  globalSearch: false,
  reportEditor: false,
  activityFeed: false,
  iaRefactor: false,
  collabLoop: false,
};

const flags = ref<FeatureFlags>(defaults);
const loaded = ref(false);

export function useFeatures() {
  async function load(): Promise<FeatureFlags> {
    if (loaded.value) return flags.value;
    try {
      const { data } = await apiClient.get<FeatureFlags>('/config/features');
      flags.value = { ...defaults, ...data };
    } catch {
      // Fall back to all-off if the endpoint is unreachable — safest posture.
      flags.value = defaults;
    } finally {
      loaded.value = true;
    }
    return flags.value;
  }

  return { flags, loaded, load };
}
